import { DeckDTO, ExerciseDTO } from '@/generated';
import {
  Box,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Delete, Edit, Info, MoreVert, Remove } from '@mui/icons-material';
import { useContext, useState } from 'react';
import { AppContext } from '@/contexts/app.context';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { isDeckEditor } from '@/screens/decks/deck-utils';
import { isExerciseEditor } from '../exercise-utils';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import { useBodyZoneListQuery, useExerciseCategoriesQuery } from '@/hooks/query-hooks';
import { StarRating } from '@/components/StarRating';
import { NavigateToExerciseDetailsButton } from '@/components/icon-buttons/NavigateToExerciseDetailsButton';
import { EditExerciseButton } from '@/components/icon-buttons/EditExerciseButton';
import { DeleteExerciseButton } from '@/components/icon-buttons/DeleteExerciseButton';
import { RemoveExerciseButton } from '@/components/icon-buttons/RemoveExerciseButton';
import { useNavigate } from 'react-router-dom';
import { pathMap } from '@/navigation/paths';
import { ExerciseMetrics } from './ExerciseMetrics';

interface CardExerciseProps {
  exercise: ExerciseDTO;
  timeout: number;
  deck?: DeckDTO;
  onClick?: () => void;
  onExerciseDeleted?: () => void;
}

const listPadding = 1;
const listGap = 0.5;

export const ExerciseCardListElement = (props: CardExerciseProps & { setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  return (
    <Grid
      sx={{ cursor: props.onClick ? 'pointer' : 'auto' }}
      onClick={(e) => {
        if (props.onClick) props.onClick();
        e.stopPropagation();
      }}
      size={{ xs: 12, md: 12 }}
    >
      <MobileExerciseListElement {...props} />
      <DesktopExerciseListElement {...props} />
    </Grid>
  );
};

interface ListElementProps extends CardExerciseProps {
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const DesktopExerciseListElement = (props: ListElementProps) => {
  const { deck, exercise, setShowDeleteModal } = props;
  const { bodyZoneList } = useBodyZoneListQuery();
  const context = useContext(AppContext);
  const user = context.state?.userProfile;
  const bodyZoneName = bodyZoneList?.find((bz) => bz.id === exercise.mainBodyZoneId)?.name ?? 'N.A.';

  return (
    <Stack
      sx={{
        display: { xs: 'none', sm: 'grid' },
        gridTemplateColumns: '40px 50px minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1fr) minmax(70px, 0.7fr) 165px 80px',
        gap: listGap,
        width: '100%',
        '& > .MuiPaper-root': { minWidth: 0, overflowWrap: 'anywhere' },
      }}
    >
      <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
        <Stack sx={{ gap: 1 }} direction={'row'}>
          {props.exercise.publiclyVisible ? <PublicIcon /> : <PublicOffIcon />}
        </Stack>
      </Paper>
      <Paper sx={{ minWidth: 50, p: listPadding }} elevation={4}>
        <ImageFromServer sx={{ width: 30, height: 30 }} id={props.exercise.imageId} thumbnail={true} alt={''} />
      </Paper>

      <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
        {props.exercise.name}
      </Paper>
      <Paper sx={{ px: 1, alignContent: 'center', justifyItems: 'flex-start', '& > .MuiStack-root': { flexWrap: 'wrap' } }}>
        <ExerciseCategories exercise={exercise} />
      </Paper>
      <Paper sx={{ px: 1, alignContent: 'center', justifyItems: 'flex-start', '& > .MuiStack-root': { flexWrap: 'wrap' } }}>
        <StarRating rating={props.exercise.difficulty} />
      </Paper>
      <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
        <Typography variant="body2">
          {bodyZoneName}
          {exercise.isUniLateral ? ', Unilateral' : ''}
        </Typography>
      </Paper>
      <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
        <ExerciseMetrics exercise={exercise} />
      </Paper>

      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} elevation={4}>
        {deck !== undefined && <NavigateToExerciseDetailsButton exerciseId={exercise.id} />}
        {user && isDeckEditor(deck, user) && <RemoveExerciseButton setShowDeleteModal={setShowDeleteModal} />}
        {user && !deck && isExerciseEditor(exercise, user) && <EditExerciseButton exerciseId={exercise.id} />}
        {user && !deck && isExerciseEditor(exercise, user) && <DeleteExerciseButton setShowDeleteModal={setShowDeleteModal} />}
      </Paper>
    </Stack>
  );
};

const MobileExerciseListElement = (props: ListElementProps) => {
  const { deck, exercise, setShowDeleteModal } = props;
  const { bodyZoneList } = useBodyZoneListQuery();
  const context = useContext(AppContext);
  const user = context.state?.userProfile;
  const bodyZoneName = bodyZoneList?.find((bz) => bz.id === exercise.mainBodyZoneId)?.name ?? 'N.A.';
  const isEditor = (deck && isDeckEditor(deck, user)) || (!deck && isExerciseEditor(exercise, user));
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const closeMenu = () => setMenuAnchor(null);
  const runMenuAction = (action: () => void) => {
    closeMenu();
    action();
  };

  return (
    <Paper sx={{ display: { xs: 'block', sm: 'none' }, p: 1.5 }} elevation={3}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <ImageFromServer sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1 }} id={exercise.imageId} thumbnail={true} alt="" />

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {exercise.publiclyVisible ? <PublicIcon fontSize="small" color="action" /> : <PublicOffIcon fontSize="small" color="action" />}
            <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
              {exercise.name}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {bodyZoneName}
            {exercise.isUniLateral ? ', Unilateral' : ''}
          </Typography>
          <StarRating rating={exercise.difficulty} />
        </Stack>

        {(deck !== undefined || isEditor) && (
          <IconButton
            aria-label={`Actions for ${exercise.name}`}
            aria-controls={menuAnchor ? `exercise-actions-${exercise.id}` : undefined}
            aria-haspopup="true"
            aria-expanded={menuAnchor ? 'true' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <MoreVert />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={2} sx={{ mt: 1.25 }}>
        <ExerciseMetrics exercise={exercise} />
      </Stack>

      <Box sx={{ mt: 1, color: 'text.secondary' }}>
        <ExerciseCategories exercise={exercise} />
      </Box>

      <Menu id={`exercise-actions-${exercise.id}`} anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} onClick={(event) => event.stopPropagation()}>
        {deck !== undefined && (
          <MenuItem onClick={() => runMenuAction(() => navigate(`${pathMap.ExerciseDetails}/${exercise.id}`))}>
            <ListItemIcon>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText>View details</ListItemText>
          </MenuItem>
        )}
        {isEditor && !deck && (
          <MenuItem onClick={() => runMenuAction(() => navigate(`${pathMap.EditExercise}/${exercise.id}`))}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {isEditor && (
          <MenuItem onClick={() => runMenuAction(() => setShowDeleteModal(true))}>
            <ListItemIcon>{deck ? <Remove fontSize="small" /> : <Delete fontSize="small" />}</ListItemIcon>
            <ListItemText>{deck ? 'Remove from deck' : 'Delete'}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export const ExerciseCategories = (props: { exercise: ExerciseDTO }) => {
  const { exercise } = props;
  const { exerciseCategories } = useExerciseCategoriesQuery(true);

  return (
    <Stack sx={{ mx: 1, gap: 1, flexDirection: 'row', fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>
      {exercise.categoryIds.map((cid) => exerciseCategories?.find((ec) => ec.id === cid)?.name).join(', ')}
      {exercise.categoryIds.length === 0 && 'No Categories'}
    </Stack>
  );
};
